import { Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { navigation } from "../data/navigation";
import { profile } from "../data/profile";
import { useScrollSpy } from "../hooks/useScrollSpy";
import { ThemeToggle } from "./ThemeToggle";

export function Header() {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [open, setOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const activeId = useScrollSpy(isHome ? navigation.map((item) => item.href.slice(1)) : []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open) {
        setOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.classList.toggle("menu-open", open);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("menu-open");
    };
  }, [open]);

  useEffect(() => {
    if (open) requestAnimationFrame(() => closeButtonRef.current?.focus());
  }, [open]);

  const hrefFor = (hash: string) => (isHome ? hash : `/${hash}`);

  return (
    <header className="site-header">
      <div className="header-inner shell">
        <Link className="brand-mark" to="/" aria-label={`${profile.name}, home`}>
          <span className="brand-monogram">{profile.initials}</span>
          <span className="brand-name">{profile.name}</span>
        </Link>

        <nav id="mobile-menu" className={`site-nav ${open ? "is-open" : ""}`} aria-label="Primary navigation">
          <div className="mobile-menu-heading">
            <span>Navigate</span>
            <button
              ref={closeButtonRef}
              className="icon-button"
              type="button"
              onClick={() => {
                setOpen(false);
                requestAnimationFrame(() => menuButtonRef.current?.focus());
              }}
              aria-label="Close menu"
            >
              <X aria-hidden="true" />
            </button>
          </div>
          {navigation.map((item) => {
            const id = item.href.slice(1);
            return (
              <a
                key={item.href}
                href={hrefFor(item.href)}
                className={activeId === id ? "active" : ""}
                aria-current={activeId === id ? "location" : undefined}
                onClick={() => setOpen(false)}
              >
                {item.label}
              </a>
            );
          })}
          <a className="mobile-cv-link" href={profile.cvRequestUrl}>
            Request CV
          </a>
        </nav>

        <div className="header-actions">
          <ThemeToggle />
          <a className="button button-small header-cv" href={profile.cvRequestUrl}>
            Request CV
          </a>
          <button
            ref={menuButtonRef}
            className="icon-button menu-button"
            type="button"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
          >
            <Menu aria-hidden="true" />
          </button>
        </div>
      </div>
      {open && <button className="menu-backdrop" type="button" onClick={() => setOpen(false)} aria-label="Close menu" />}
    </header>
  );
}
